
import {defineComponent} from "vue";
import ProductService from "@/services/api/product.service";
import {toast} from "vue3-toastify";
import InfigoIframe from "@/components/iframe/index.vue";
import ShoppingCartItemState from "@/services/cache/shopping-cart-item-state";
import InfigoLoading from "@/components/shared/loading.vue";
import type {
  InfigoProductAttributeValueInfo
} from "@infigo-official/types-for-api/src/models/InfigoProductAttributeValueInfo";
import Title from "@/components/layout/title.vue";
import {InfigoProductType} from "@/types/infigo-product.type";
import {BasketItem} from "@/types/demo/basket-item";
import {ShoppingCartItem} from "@/types/iframe/infigo-job-response.type";
import SessionState from "@/services/cache/session-state";
import CustomerService from "@/services/api/customer.service";
import CatfishEditorCommunication from "@/services/catfish-editor-communication";

export default defineComponent({
  components: {
    Title,
    InfigoIframe: InfigoIframe,
    InfigoLoading: InfigoLoading
  },
  data() {
    return {
      loading: true,
      canGoToBasket: false,
      iframeProductId: null as number | null,
      products: [] as Array<ProductType>,
      openIframe: false,
      enableEditOptions: true,
      attributeTypes: InfigoAttributeType,
      attributeSelection: {} as Record<string, string>,
      landingPageSrc: "",
      landingDestroyCallback: null as any,
      hideAddToBasketButton: false,
      isJobCompleted: false,
      currentJobId: null as number | null,
      itemAddedToBasket: false
    };
  },
  computed: {
    showExternalButtons(): boolean {
      return this.openIframe && this.hideAddToBasketButton;
    },
    isMultipartOrMegaEditProduct(): boolean {
      if (!this.iframeProductId) {
        return false;
      }
      const selectedProduct = this.products.find(q => q.id == this.iframeProductId);
      return selectedProduct?.type === InfigoProductType.MultiPart ||
             selectedProduct?.type === InfigoProductType.Dynamic;
    }
  },
  async created() { // Make created method async
    await this.init();
  },
  unmounted() {
    if (this.landingDestroyCallback) {
      this.landingDestroyCallback();
    }
  },
  methods: {
    getAttributes() {
      if (!this.iframeProductId) {
        return [];
      }

      return this.products?.find(q => q.id == this.iframeProductId)?.attributes ?? [];
    },
    onAttributeChanged(name: string, value: string) {
      this.attributeSelection[name] = value;
    },
    onProductChanged(productId: number) {
      this.attributeSelection = {};

      const selectedProduct = this.products.find(q => q.id == productId);
      if (!selectedProduct) {
        return;
      }

      selectedProduct.attributes.forEach(attr => {
        if (attr.values.length > 0) {
          this.attributeSelection[attr.name] = attr.values.find(q => q.isDefault == true)?.name ?? attr.values[0]?.name;
        }
      });
    },
    async init() {
      try {
        const productsResponse = await ProductService.getAll();

        const supportedProductTypes = [InfigoProductType.Dynamic, InfigoProductType.Static, InfigoProductType.Normal, InfigoProductType.MultiPart];

        this.products = productsResponse.data
            .filter(it => supportedProductTypes.includes(it.Type as number))
            .map(it => {
              return {
                id: it.Id as number,
                name: it.Name as string,
                type: (it.Type as number) as InfigoProductType,
                thumbnailUrls: it.ThumbnailUrls,
                attributes: it?.ProductAttributes
                    ?.filter(q => !q.Name?.startsWith("Catfish_"))
                    ?.map((q: any) => {
                      return {
                        name: q.Name,
                        description: q.Description,
                        type: q.AttributeControlType,
                        values: q.ProductAttributeValues?.map((v: InfigoProductAttributeValueInfo) => {
                          return {
                            id: v.Id,
                            name: v.Name,
                            isDefault: v.IsPreSelected
                          }
                        }) ?? []
                      } as AttributeType
                    }) ?? []
              } as ProductType
            });

      } catch (err) {
        console.log(err);
        toast("Error loading products", {
          type: "error"
        });
      } finally {
        this.loading = false; // Hide loading overlay once data is fetched
      }
    },
    canDesign() {
      const selectedProduct = this.products.find(q => q.id == this.iframeProductId);

      if (!selectedProduct) {
        return;
      }

      const allowedToDesign = [InfigoProductType.Dynamic, InfigoProductType.MultiPart];
      return allowedToDesign.includes(selectedProduct.type);
    },
    addToBasket(){
      debugger;
      const selectedProduct = this.products.find(q => q.id == this.iframeProductId);
      if (!selectedProduct) {
        toast("Product not found", {position: "top-right", type: "error"});
        return;
      }

      const basketItems = ShoppingCartItemState.loadCartFromLocalStorage();
      const index = basketItems.findIndex((it) => it.productId == selectedProduct.id);
      if (index > -1){
        const existingItemInBasket = basketItems[index];
        existingItemInBasket.quantity += 1;
        ShoppingCartItemState.updateItem(index, existingItemInBasket);
        toast.success(`Quantity updated with success for ${selectedProduct.name}`, {position: "top-right"});

        setTimeout(() => {
          this.$router.push("/shopping-list");
        }, 1000);

        return;
      }

      const newItemBasket: BasketItem = {
        jobId:  "",
        quantity: 1,
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        productSKU: selectedProduct.name,
        productType: selectedProduct.type,
        thumbnailUrls: selectedProduct.thumbnailUrls,
        customerGuid: SessionState.customerId ?? "",
      }

      ShoppingCartItemState.addItem(newItemBasket);

      toast.success("Item added to basket", {position: "top-right"});
      setTimeout(() => {
        this.$router.push("/shopping-list");
      }, 1000);
    },
    async openLandingPage() {
      const platformUrl = SessionState.platformUrl;
      const customerId = SessionState.customerId;

      if (!platformUrl || !customerId || !this.iframeProductId) {
        toast.error("Platform URL, Customer ID and Product are required");
        return;
      }

      this.loading = true;

      try {
        const urlObj = new URL(platformUrl);
        const baseUrl = `${urlObj.protocol}//${urlObj.host}`;
        const storefrontPath = urlObj.pathname.replace(/\/+$/, '');
        const parentOrigin = window.location.origin;

        const productPageUrl = `${storefrontPath}/p/${this.iframeProductId}?editOptions=true&enableEditOptions=${this.enableEditOptions}&bMode=Iframe&bUrl=${encodeURIComponent(parentOrigin)}`;

        const ssoResponse = await CustomerService.getSSOUrl(customerId, productPageUrl);
        const loginUrl = ssoResponse.data.LoginUrl || "";

        this.landingPageSrc = `${baseUrl}${loginUrl}`;
        this.openIframe = false;
        this.canGoToBasket = true;

        if (this.landingDestroyCallback) {
          this.landingDestroyCallback();
        }

        this.landingDestroyCallback = CatfishEditorCommunication.RegisterForCatfishEditorEvent(
          (method: string, data: any) => {
            switch (method) {
              case CatfishEditorCommunication.MessageConstants.InfigoItemAddedToBasket:
              case CatfishEditorCommunication.MessageConstants.InfigoItemAddedToSavedProjects:
                this.addDesignJob(data as ShoppingCartItem);
                break;
              default:
                break;
            }
          },
          baseUrl
        );
      } catch (e) {
        console.error("Failed to open landing page", e);
        toast.error("Failed to open landing page");
      } finally {
        this.loading = false;
      }
    },
    openIframeSubmit() {
      if (this.openIframe) {
        this.init();
      }
      this.landingPageSrc = "";
      this.openIframe = true;
      this.loading = true;
      this.canGoToBasket = true;
    },
    iframeLoaded() {
      this.loading = false;
    },
    addDesignJob(item: ShoppingCartItem) {
      console.log("Item added to basket:", item);
      const newItemBasket: BasketItem = {
        jobId: item.Job.Id,
        quantity: item.Job.Quantity,
        productId: item.Product.Id,
        productName: item.Product.Name,
        productSKU: item.Product.Sku,
        productType: InfigoProductType.Dynamic,
        thumbnailUrls: item.Images.Thumbnails,
        customerGuid: item.Customer.Guid,
      }

      ShoppingCartItemState.addItem(newItemBasket);
      this.canGoToBasket = true;
      this.itemAddedToBasket = true;
    },
    getHideElements(): string[] {
      const elements: string[] = [];
      if (this.hideAddToBasketButton) {
        elements.push('addToBasket');
      }
      return elements;
    },
    onJobChanged(data: any) {
      console.log('Job changed:', data);
      this.isJobCompleted = data.isCompleted || false;
      this.currentJobId = data.jobId || null;
    },
    handleExternalAddToBasket() {
      const iframe = this.$refs.infigoIframe as any;
      if (iframe && iframe.triggerExternalAddToBasket) {
        iframe.triggerExternalAddToBasket();
      }
    }
  }
});

enum InfigoAttributeType {
  DropdownList = 1,
  RadioList = 2,
  Checkboxes = 3,
  TextBox = 4,
  MultilineTextbox = 10,
  Datepicker = 20,
  FileUpload = 30,
  Info = 40,
  HtmlSchema = 50,
  System = 60,
  Hidden = 999
}

interface AttributeType{
  name: string;
  description: string;
  type: InfigoAttributeType;
  values: {id: number, name: string, isDefault: boolean}[]
}

interface ProductType {
  id: number;
  name: string;
  attributes: AttributeType[],
  type: InfigoProductType,
  thumbnailUrls: string[]
}

